import type { CompanyRoleDto, CompanyRolesState, SystemExecutor } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, PageTitle, SectionLoader, SectionToggle } from '../../ui';
import { useLevels } from './Livelli';
import { SectionQuestions } from './SectionQuestions';

const CLASS_LABEL: Record<string, string> = { micro: 'microimpresa', piccola: 'piccola impresa', media: 'media impresa', grande: 'grande impresa' };
const KIND_LABEL: Record<string, string> = { organo: 'organo', incarico_legge: 'incarico di legge', funzione: 'funzione', mestiere: 'mestiere' };

/** Chi ricopre il ruolo: le persone assegnate, poi le cariche della visura; altrimenti è scoperto. */
function holders(r: CompanyRoleDto): string[] {
  return [...r.persone.map((p) => p.nome), ...r.cariche.filter((c) => !r.persone.some((p) => p.nome === c.nome)).map((c) => `${c.nome} (${c.carica})`)];
}

/**
 * I ruoli dell'azienda (ADR 0207): composti dalla libreria nella Fase 0, per aree e reparti (ADR 0206 § 10), con a chi rispondono, chi li copre di
 * solito a questa dimensione e chi li ricopre davvero. Le domande della sezione decidono i ruoli da confermare.
 */
export function RolesPage() {
  const { t, tenant, isManager, livello } = useTenant();
  const levels = useLevels();
  const qc = useQueryClient();
  const q = useTenantQuery<CompanyRolesState>(['roles'], '/company-roles');
  const systems = useTenantQuery<{ data: SystemExecutor[] }>(['systems'], '/systems', { enabled: isManager });
  const [showRemoved, setShowRemoved] = useState(false);
  // Le aree si aprono una alla volta, quando servono.
  const [openAreas, setOpenAreas] = useState<Set<string>>(new Set());
  const refresh = () => qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'roles'] });
  const update = useMutation({
    mutationFn: ({ key, stato }: { key: string; stato: 'vale' | 'non_serve' }) => api(t(`/company-roles/${encodeURIComponent(key)}`), { method: 'PATCH', body: { stato } }),
    onSuccess: refresh,
  });
  const compose = useMutation({ mutationFn: () => api(t('/company-roles/compose'), { body: {} }), onSuccess: refresh });

  if (q.isLoading) return <SectionLoader />;
  if (!q.data) return <ErrorText>{q.error ? errorMessage(q.error) : 'Ruoli non disponibili'}</ErrorText>;
  // Dentro una sede i suoi ruoli: gli incarichi di luogo e quelli dei mestieri che vi si svolgono (ADR 0210 § 7).
  const site = levels.site ? livello : null;
  const ruoli = site ? q.data.ruoli.filter((r) => r.sedi_id.includes(site)) : q.data.ruoli;
  const { aree, reparti, classe } = q.data;
  // I numeri del livello: dentro una sede, dei suoi ruoli.
  const scoperti = site ? ruoli.filter((r) => r.stato === 'vale' && !r.persone.length && !r.cariche.length && r.di_solito !== 'esterno').length : q.data.scoperti;
  const da_confermare = site ? ruoli.filter((r) => r.stato === 'da_confermare').length : q.data.da_confermare;
  // I nomi da tutti i ruoli: un ruolo della sede risponde o è coperto da un ruolo dell'azienda.
  const byKey = new Map(q.data.ruoli.map((r) => [r.chiave, r]));
  const name = (k: string | null) => (k === 'lavoratore' ? 'uno dei lavoratori, designato' : k ? (byKey.get(k)?.nome ?? k) : null);
  // Aree, reparti e ruoli in ordine alfabetico; «Altri ruoli» in fondo.
  const byName = (a: { nome: string }, b: { nome: string }) => a.nome.localeCompare(b.nome, 'it');
  const live = ruoli.filter((r) => r.stato !== 'non_serve').sort(byName);
  const removed = ruoli.filter((r) => r.stato === 'non_serve').sort(byName);
  const groups = [...[...aree].sort(byName).map((a) => ({ ...a, items: live.filter((r) => r.area === a.chiave) })), { chiave: 'altri', nome: 'Altri ruoli', responsabile: null, items: live.filter((r) => !r.area || !aree.some((a) => a.chiave === r.area)) }].filter((g) => g.items.length);
  // Le incompatibilità tra ruoli che toccano la stessa persona assegnata o la stessa carica.
  const clashes = live.flatMap((r) =>
    r.incompatibile.flatMap((i) => {
      const other = byKey.get(i.ruolo);
      if (!other || other.stato === 'non_serve') return [];
      const same = holders(r).filter((h) => holders(other).includes(h));
      return same.length && r.chiave < other.chiave ? [{ a: r.nome, b: other.nome, chi: same.join(', '), ...i }] : [];
    }),
  );

  const roleItem = (r: CompanyRoleDto) => {
    const who = holders(r);
    const assignedSystems = (systems.data?.data ?? []).filter((s) => s.active && s.roles.some((assignment) => assignment.role_key === r.chiave && (!site || assignment.site_ids === null || assignment.site_ids.includes(site))));
    return (
      <li key={r.chiave} className="py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{r.nome}</span>
          {r.tipo && r.tipo !== 'funzione' && r.tipo !== 'mestiere' && <Badge>{KIND_LABEL[r.tipo]}</Badge>}
          {r.stato === 'da_confermare' && <Badge tone="amber">da confermare</Badge>}
          {r.di_solito === 'esterno' && <Badge tone="blue">di solito esterno{r.esterno_tipico ? `: ${r.esterno_tipico}` : ''}</Badge>}
          {r.di_solito === 'entrambi' && <Badge tone="blue">interno o esterno</Badge>}
          {isManager && r.origine === 'libreria' && (
            <button type="button" className="ml-auto text-xs text-ink/50 hover:text-ink" onClick={() => update.mutate({ key: r.chiave, stato: 'non_serve' })}>
              Non serve
            </button>
          )}
        </div>
        {r.descrizione && <p className="mt-0.5 text-xs text-ink/70">{r.descrizione}</p>}
        <p className="mt-1 text-xs text-ink/60">
          {who.length ? (
            <>Lo ricopre: {who.join(', ')}</>
          ) : r.di_solito === 'esterno' ? (
            <>Da affidare a un esterno</>
          ) : (
            <span className="text-amber-700">Da assegnare</span>
          )}
          {!who.length && r.coperto_da && <> · di solito lo copre: {name(r.coperto_da)}</>}
          {r.risponde_a && <> · risponde a: {name(r.risponde_a)}</>}
          {r.sedi.length > 0 && <> · sedi: {r.sedi.join(', ')}</>}
          {r.norma && <> · {r.norma}</>}
        </p>
        {assignedSystems.length > 0 && (
          <p className="mt-1 text-xs text-ink/60">
            Sistemi configurati: {assignedSystems.map((s) => {
              const assignment = s.roles.find((a) => a.role_key === r.chiave)!;
              return `${s.name}${assignment.sites.length ? ` (${assignment.sites.join(', ')})` : ' (tutta l’azienda)'}`;
            }).join('; ')}. <a href={`#/t/${tenant.id}/sistemi`} className="text-accent hover:underline">Configura</a>
          </p>
        )}
      </li>
    );
  };

  return (
    <>
      <PageTitle
        title={levels.site ? `Ruoli · ${levels.site.name}` : 'Ruoli'}
        subtitle={levels.site ? 'Gli incarichi di questa sede e i ruoli dei mestieri che vi si svolgono. I ruoli di tutta l’azienda (direzione, amministrazione, fisco) si vedono in «Tutta l’azienda».' : 'Il lavoro dell’azienda diviso in ruoli, dalla libreria del suo mestiere. Ogni procedura dirà quale ruolo la svolge: chi ricopre il ruolo, la svolge.'}
        actions={isManager ? <Button variant="ghost" onClick={() => compose.mutate()} disabled={compose.isPending}>Ricomponi dalla libreria</Button> : undefined}
      />
      <SectionQuestions sezione="ruoli" />
      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        <Badge tone="blue">{CLASS_LABEL[classe] ?? classe}</Badge>
        <Badge>{live.length} ruoli</Badge>
        {scoperti > 0 && <Badge tone="amber">{scoperti} da assegnare</Badge>}
        {da_confermare > 0 && <Badge tone="amber">{da_confermare} da confermare</Badge>}
      </div>
      {compose.error && <ErrorText>{errorMessage(compose.error)}</ErrorText>}
      {update.error && <ErrorText>{errorMessage(update.error)}</ErrorText>}
      {systems.error && <ErrorText>{errorMessage(systems.error)}</ErrorText>}
      {clashes.length > 0 && (
        <Card className="mb-4 border-red-200 bg-red-50">
          <h2 className="mb-1 text-sm font-semibold text-red-800">Ruoli da tenere separati</h2>
          <ul className="space-y-1 text-sm text-red-900">
            {clashes.map((c) => (
              <li key={`${c.a}-${c.b}`}>
                {c.a} e {c.b} sono di {c.chi}: {c.nota}
                {c.base === 'legge' ? ` (${c.norma})` : ' (buona prassi)'}.
              </li>
            ))}
          </ul>
        </Card>
      )}
      {!live.length && <Empty>I ruoli si compongono con il gestionale, dopo la lettura della visura.</Empty>}
      <div className="space-y-4">
        {groups.map((g) => (
          <Card key={g.chiave} className={openAreas.has(g.chiave) ? '' : '!py-4'}>
            <SectionToggle title={g.nome} count={g.items.length} open={openAreas.has(g.chiave)} onToggle={() => setOpenAreas((s) => new Set(s.has(g.chiave) ? [...s].filter((x) => x !== g.chiave) : [...s, g.chiave]))} />
            {openAreas.has(g.chiave) && (
              <>
                {g.responsabile && byKey.get(g.responsabile) && <p className="mb-2 mt-1 text-xs text-ink/60">Ne risponde: {name(g.responsabile)}</p>}
                <ul className="divide-y divide-ink/10">{g.items.filter((r) => !r.reparto).map(roleItem)}</ul>
                {[...reparti]
                  .sort(byName)
                  .filter((p) => p.area === g.chiave && g.items.some((r) => r.reparto === p.chiave))
                  .map((p) => (
                    <div key={p.chiave} className="mt-3 border-l-2 border-ink/10 pl-3">
                      <h3 className="text-sm font-semibold text-ink/80">{p.nome}</h3>
                      <p className="mb-1 text-xs text-ink/60">
                        {p.descrizione}
                        {byKey.get(p.responsabile) && <> · ne risponde: {name(p.responsabile)}</>}
                      </p>
                      <ul className="divide-y divide-ink/10">{g.items.filter((r) => r.reparto === p.chiave).map(roleItem)}</ul>
                    </div>
                  ))}
              </>
            )}
          </Card>
        ))}
      </div>
      {removed.length > 0 && (
        <div className="mt-4 px-1">
          <button type="button" className="text-xs text-ink/55 underline decoration-ink/25 underline-offset-2 hover:text-ink" onClick={() => setShowRemoved((v) => !v)}>
            {showRemoved ? 'Nascondi' : 'Mostra'} {removed.length === 1 ? 'il ruolo che non serve' : `i ${removed.length} ruoli che non servono`}
          </button>
          {showRemoved && (
            <ul className="mt-2 divide-y divide-ink/10">
              {removed.map((r) => (
                <li key={r.chiave} className="flex items-center gap-2 py-1.5 text-sm text-ink/60">
                  <span>{r.nome}</span>
                  {r.motivo && <span className="text-xs text-ink/50">{r.motivo}</span>}
                  {isManager && (
                    <button type="button" className="ml-auto text-xs text-ink/50 hover:text-ink" onClick={() => update.mutate({ key: r.chiave, stato: 'vale' })}>
                      Ripristina
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
